'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import type { Variants } from 'motion/react';
import {
  RiArrowDownLine,
  RiChat3Line,
  RiCheckLine,
  RiCloseLine,
  RiLinkM,
  RiPauseCircleLine,
  RiRobot2Line,
  RiSearchEyeLine,
  RiTimeLine,
  RiUser3Line,
} from '@remixicon/react';
import { useTranslations } from 'next-intl';

import { dur, ease, spring, springFade, stagger } from '@/lib/motion';
import { hl } from '@/lib/rich-text';

/**
 * "Nasıl çalışır" — sayfanın ilk açık bölümü ve tek etkileşimli parçası.
 *
 * ── Neden anlatı değil gösterim ───────────────────────────────────────────
 *
 * Buraya gelen kişi ajanın ne olduğunu bilmiyor olabilir: "reklam hesabıma
 * bağlı bir sohbet botu" cümlesi tek başına ya fazla soyut ya fazla korkutucu.
 * Üç paragraf yazmak yerine kişinin kendi eliyle bir soru sorup sonucu
 * görmesi konuyu tek hamlede kapatıyor — üstelik en kritik iddia (ONAY
 * ALMADAN dokunmaz) burada iddia olmaktan çıkıp kullanıcının bizzat bastığı
 * bir düğmeye dönüşüyor.
 *
 * Üç soru bilinçli olarak ÜÇ FARKLI sonuç veriyor; kapsamı liste yapmadan
 * anlatan şey bu:
 *   spend  → yalnızca okur, hiçbir şey değişmez
 *   stop   → bir değişiklik önerir, onay ister
 *   weekly → Operatör'e görev kurulur; görev bir not, onay istemez (14 Eki
 *            2026'ya kadar burada "otomasyon kuralı doğar" vardı — kural
 *            ekranları 5 Eki'de kalktı, AI artık kural kurmuyor)
 *
 * ── Soldaki üç adım ───────────────────────────────────────────────────────
 *
 * Ray dekoratif değil: gösterim ilerledikçe hangi adımda olduğumuzu söylüyor.
 * "Ajan verinize bakar" satırı tam da veri okunurken yanıyor, "siz karar
 * verirsiniz" tam da düğmeler belirdiğinde. Hareket burada bilgi taşıyor
 * (DESIGN.md §4) — ama tek başına değil: her adımın metni zaten yazılı ve
 * durum `aria-current` ile de veriliyor.
 *
 * `spend` sorusunda üçüncü adım hiç yanmıyor, sönük kalıyor. Doğrusu bu:
 * o soruda alınacak bir karar yok ve sahte bir "tamamlandı" işareti basmak
 * bölümün kendi cümlesini yalanlardı.
 *
 * ── Zamanlayıcı ve azaltılmış hareket ─────────────────────────────────────
 *
 * Sahne `useEffect` içindeki üç zamanlayıcıyla ilerliyor; soru değişince ya
 * da bileşen sökülünce hepsi temizleniyor. Kullanıcının basabildiği tek an
 * (`decide`) son zamanlayıcıdan SONRA başlıyor, dolayısıyla bir zamanlayıcının
 * kullanıcının kararını ezmesi mümkün değil.
 *
 * Azaltılmış hareket tercihinde bekleme süreleri neredeyse sıfıra iniyor ve
 * okuma sırasındaki süpürme döngüsü hiç basılmıyor — `<MotionConfig>` sonsuz
 * döngüleri kapatmıyor, o elle karşılanıyor (DESIGN.md §1).
 */

type Key = 'spend' | 'stop' | 'weekly';

/** Ekranda göründükleri sıra; soru düğmeleri de bu sırayla basılıyor. */
const KEYS: readonly Key[] = ['spend', 'stop', 'weekly'];

/** Sohbetin durumu. `rank` ile sıralanıyor — ray ve gövde ondan besleniyor. */
type Phase = 'idle' | 'ask' | 'read' | 'answer' | 'decide' | 'approved' | 'rejected';

const RANK: Record<Phase, number> = {
  idle: 0,
  ask: 1,
  read: 2,
  answer: 3,
  decide: 4,
  approved: 5,
  rejected: 5,
};

/**
 * Yalnızca `stop` yazma aksiyonu öneriyor. `spend` okumakla, `weekly` görev
 * kurmakla bitiyor — görev kurmak onay istemez; görev çalışıp bir değişiklik
 * gerektirirse o değişiklik çalışma alanının moduna göre davranır.
 */
const ACTION: Record<Key, boolean> = { spend: false, stop: true, weekly: false };

/** Öneri kartının başındaki ikon — aksiyonun ne olduğunu tek bakışta söyler. */
const ACTION_ICON: Record<string, typeof RiPauseCircleLine> = {
  stop: RiPauseCircleLine,
};

/** `spend` yanıtındaki tablonun satırları; sıra = harcama sırası. */
const ROWS = ['summer', 'retarget', 'brand'] as const;

/**
 * Sahnenin ritmi (ms). Okuma beklemesi gerçek bir işi taklit ediyor: anında
 * beliren yanıt "hazır metin" gibi duruyor, veriye bakıldığı hissini vermiyor.
 * Toplam 2,1 sn — kullanıcının elini düğmeden çekmeyeceği sınırın içinde.
 */
const BEAT = { read: 420, answer: 1500, decide: 2150 } as const;
/** Azaltılmış hareket: sıra korunuyor, bekleme kalkıyor. */
const BEAT_CALM = { read: 0, answer: 90, decide: 180 } as const;

const headV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base } },
};

const copyV: Variants = {
  hidden: { opacity: 0, y: 10, filter: 'blur(5px)' },
  show: { opacity: 1, y: 0, filter: 'blur(0px)', transition: springFade(spring.default) },
};

/* Koyu sahne ağır bir yüzey: `soft` yayı (mass 1.1) onu kartondan değil
   metalden yapıyor — control.tsx'teki maketlerle aynı gerekçe. */
const stageV: Variants = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: springFade(spring.soft, dur.base) },
};

const stepsV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base, delayChildren: 0.08 } },
};

const stepV: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

/* Sohbete düşen satırlar. Yol kısa (8px) ve blur yok: aynı anda üç dört parça
   giriyor, blur bütçesi (DESIGN.md §6) sahne için ayrıldı. */
const lineV: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
  exit: { opacity: 0, y: -6, transition: { duration: dur.instant } },
};

/* Onay kartı BİLİNÇLİ olarak daha ağır giriyor: para harcayan karar, sohbet
   satırıyla aynı hızda düşerse aynı önemde okunuyor (DESIGN.md §4). */
const cardV: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: springFade(spring.soft, dur.base) },
};

export function AgentTalk() {
  const t = useTranslations('featuresAgent.talk');
  const uid = useId();
  const reduce = useReducedMotion() ?? false;

  const [active, setActive] = useState<Key | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');

  /* Aynı soruya tekrar basıldığında sahne baştan oynasın diye: anahtar
     değişmese bile bu sayaç değişiyor ve efekt yeniden kuruluyor. */
  const [run, setRun] = useState(0);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    if (!active) return;

    const beat = reduce ? BEAT_CALM : BEAT;
    const at = (ms: number, next: Phase) =>
      timers.current.push(setTimeout(() => setPhase(next), ms));

    setPhase('ask');
    at(beat.read, 'read');
    at(beat.answer, 'answer');
    if (ACTION[active]) at(beat.decide, 'decide');

    const running = timers.current;
    return () => {
      running.forEach(clearTimeout);
      timers.current = [];
    };
  }, [active, run, reduce]);

  const start = (key: Key) => {
    setActive(key);
    setRun((n) => n + 1);
  };

  const reset = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setActive(null);
    setPhase('idle');
  };

  const rank = RANK[phase];
  const item = active ? `items.${active}` : null;
  const ActionIcon = active ? ACTION_ICON[active] : undefined;

  /** Rayın üç adımı. `skip` yalnızca kararsız biten soruda (spend) kullanılıyor. */
  const stepState = (i: number): 'idle' | 'active' | 'done' | 'skip' => {
    if (i === 0) return rank === 0 ? 'idle' : rank === 1 ? 'active' : 'done';
    if (i === 1) return rank < 2 ? 'idle' : rank === 2 ? 'active' : 'done';
    if (active && !ACTION[active]) return rank >= 3 ? 'skip' : 'idle';
    return rank < 4 ? 'idle' : rank === 4 ? 'active' : 'done';
  };

  const STEPS = ['ask', 'read', 'decide'] as const;

  return (
    <section className="talk" aria-labelledby={`${uid}-title`}>
      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <div className="talk-inner">
            <motion.div className="talk-head" variants={headV} initial={false} animate="show">
              <motion.p className="talk-eyebrow" variants={copyV}>
                {t('eyebrow')}
              </motion.p>
              <motion.h2 className="talk-title" id={`${uid}-title`} variants={copyV}>
                {t.rich('title', { hl })}
              </motion.h2>
              <motion.p className="talk-lead" variants={copyV}>
                {t('lead')}
              </motion.p>
            </motion.div>

            <div className="talk-layout">
              {/* ---------- Üç adım ---------- */}
              <motion.ol className="talk-steps" variants={stepsV} initial={false} animate="show">
                {STEPS.map((key, i) => {
                  const state = stepState(i);
                  return (
                    <motion.li
                      key={key}
                      className={`talk-step talk-step--${state}`}
                      variants={stepV}
                      aria-current={state === 'active' ? 'step' : undefined}
                    >
                      <span className="talk-step-mark" aria-hidden="true">
                        <span className="talk-step-num">{i + 1}</span>
                        {/* Nokta değil halka doluyor: adım "tamamlandı" bilgisini
                            renk tek başına taşımasın diye işaret de değişiyor. */}
                        <AnimatePresence initial={false}>
                          {state === 'done' && (
                            <motion.span
                              key="tick"
                              className="talk-step-tick"
                              initial={{ opacity: 0, scale: 0.6 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.6 }}
                              transition={spring.snap}
                            >
                              <RiCheckLine size={13} />
                            </motion.span>
                          )}
                        </AnimatePresence>
                      </span>
                      <span className="talk-step-body">
                        <span className="talk-step-title">{t(`steps.${key}.title`)}</span>
                        <span className="talk-step-desc">{t(`steps.${key}.desc`)}</span>
                      </span>
                    </motion.li>
                  );
                })}
              </motion.ol>

              {/* ---------- Sahne ---------- */}
              <motion.div className="talk-stage" variants={stageV} initial={false} animate="show">
                {/* Üst çubuk ajanın adını TEKRARLAMIYOR — o zaten her yanıtın
                    başında yazılı. Onun yerine bağlamı söylüyor: ajan hangi
                    hesaba bakıyor. Gerçek üründe workspace bağlamı (bağlı
                    hesaplar, Operatör görevleri, plan limiti) sistem prompt'unda
                    duruyor; burada onun görünen ucu var. */}
                <div className="talk-bar">
                  <p className="talk-bar-who">
                    <RiLinkM size={15} aria-hidden="true" />
                    {t('account')}
                  </p>
                  <AnimatePresence initial={false}>
                    {active && (
                      <motion.button
                        key="reset"
                        type="button"
                        className="talk-reset"
                        onClick={reset}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: dur.fast, ease: ease.out }}
                      >
                        {t('again')}
                      </motion.button>
                    )}
                  </AnimatePresence>
                </div>

                {/* Sahnenin gövdesi: ekran okuyucu yeni düşen satırları duysun
                    diye kibar bir canlı bölge. */}
                <div
                  className={`talk-thread${active ? '' : ' talk-thread--idle'}`}
                  aria-live="polite"
                >
                  <AnimatePresence mode="popLayout" initial={false}>
                    {!active && (
                      <motion.div
                        key="empty"
                        className="talk-empty"
                        variants={lineV}
                        initial="hidden"
                        animate="show"
                        exit="exit"
                      >
                        <RiChat3Line size={22} aria-hidden="true" />
                        <p className="talk-empty-title">{t('emptyTitle')}</p>
                        <p className="talk-empty-hint">{t('promptHint')}</p>
                        {/* Ok, gözü soruların olduğu yere indiriyor: boş sahnede
                            tek yapılacak iş orada. */}
                        <RiArrowDownLine
                          className="talk-empty-arrow"
                          size={18}
                          aria-hidden="true"
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {active && item && (
                    <>
                      {/* --- Soru --- */}
                      <motion.p
                        key={`${active}-${run}-q`}
                        className="talk-said talk-said--you"
                        variants={lineV}
                        initial="hidden"
                        animate="show"
                      >
                        <span className="talk-who">
                          <RiUser3Line size={14} aria-hidden="true" />
                          {t('you')}
                        </span>
                        <span className="talk-said-text">{t(`${item}.q`)}</span>
                      </motion.p>

                      {/* --- Veriyi okuma --- */}
                      <AnimatePresence initial={false}>
                        {rank >= 2 && (
                          <motion.p
                            key="tool"
                            className={`talk-tool${rank >= 3 ? ' talk-tool--done' : ''}`}
                            variants={lineV}
                            initial="hidden"
                            animate="show"
                          >
                            <span className="talk-tool-icon" aria-hidden="true">
                              {rank >= 3 ? (
                                <RiCheckLine size={14} />
                              ) : (
                                <RiSearchEyeLine size={14} />
                              )}
                            </span>
                            <span className="talk-tool-text">
                              {rank >= 3 ? t(`${item}.tool`) : t('reading')}
                            </span>
                            {/* Süpürme yalnızca okuma sürerken ve yalnızca
                                hareket kısıtlı değilken; sonsuz döngü elle
                                kapatılıyor (DESIGN.md §1). */}
                            {rank === 2 && !reduce && (
                              <span className="talk-sweep" aria-hidden="true">
                                <motion.span
                                  className="talk-sweep-run"
                                  animate={{ x: ['-120%', '320%'] }}
                                  transition={{ duration: 1.1, ease: 'linear', repeat: Infinity }}
                                />
                              </span>
                            )}
                          </motion.p>
                        )}
                      </AnimatePresence>

                      {/* --- Yanıt --- */}
                      <AnimatePresence initial={false}>
                        {rank >= 3 && (
                          <motion.div
                            key="answer"
                            className="talk-said talk-said--agent"
                            variants={lineV}
                            initial="hidden"
                            animate="show"
                          >
                            <span className="talk-who">
                              <RiRobot2Line size={14} aria-hidden="true" />
                              {t('agent')}
                            </span>
                            <p className="talk-said-text">{t(`${item}.a`)}</p>

                            {active === 'spend' && (
                              <table className="talk-tbl">
                                <thead>
                                  <tr>
                                    <th scope="col">{t('table.campaign')}</th>
                                    <th scope="col">{t('table.spend')}</th>
                                    <th scope="col">{t('table.share')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {ROWS.map((row) => (
                                    <tr key={row}>
                                      <th scope="row">{t(`${item}.rows.${row}.name`)}</th>
                                      <td className="talk-tbl-num">
                                        {t(`${item}.rows.${row}.spend`)}
                                      </td>
                                      <td className="talk-tbl-num">
                                        {t(`${item}.rows.${row}.share`)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* --- Sonuç: ya "hiçbir şey değişmedi" ya onay kartı --- */}
                      <AnimatePresence initial={false}>
                        {rank >= 3 && !ACTION[active] && (
                          <motion.p
                            key="outcome"
                            className="talk-outcome"
                            variants={lineV}
                            initial="hidden"
                            animate="show"
                          >
                            <RiCheckLine size={15} aria-hidden="true" />
                            {t(`${item}.outcome`)}
                          </motion.p>
                        )}

                        {rank >= 4 && ACTION[active] && ActionIcon && (
                          <motion.div
                            key="card"
                            className={`talk-card${
                              phase === 'approved'
                                ? ' talk-card--done'
                                : phase === 'rejected'
                                  ? ' talk-card--void'
                                  : ''
                            }`}
                            variants={cardV}
                            initial="hidden"
                            animate="show"
                            layout
                            transition={spring.soft}
                          >
                            <motion.p className="talk-card-tag" layout="position">
                              <RiRobot2Line size={14} aria-hidden="true" />
                              {t('proposal')}
                            </motion.p>

                            <motion.p className="talk-card-action" layout="position">
                              <ActionIcon size={18} aria-hidden="true" />
                              {t(`${item}.card.action`)}
                            </motion.p>

                            <motion.p className="talk-card-target" layout="position">
                              {t(`${item}.card.target`)}
                            </motion.p>

                            <motion.p className="talk-card-reason" layout="position">
                              {t(`${item}.card.reason`)}
                            </motion.p>

                            <AnimatePresence mode="wait" initial={false}>
                              {phase === 'decide' && (
                                <motion.div
                                  key="ask"
                                  className="talk-card-foot"
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  transition={{ duration: dur.fast, ease: ease.out }}
                                >
                                  <p className="talk-card-state">
                                    <RiTimeLine size={15} aria-hidden="true" />
                                    {t('pending')}
                                  </p>
                                  <p className="talk-card-btns">
                                    <button
                                      type="button"
                                      className="talk-btn talk-btn--ghost"
                                      onClick={() => setPhase('rejected')}
                                    >
                                      {t('reject')}
                                    </button>
                                    <button
                                      type="button"
                                      className="talk-btn talk-btn--primary"
                                      onClick={() => setPhase('approved')}
                                    >
                                      {t('approve')}
                                    </button>
                                  </p>
                                </motion.div>
                              )}

                              {(phase === 'approved' || phase === 'rejected') && (
                                <motion.p
                                  key="done"
                                  className={`talk-card-result${
                                    phase === 'approved' ? ' talk-card-result--yes' : ''
                                  }`}
                                  initial={{ opacity: 0, y: 6 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0 }}
                                  transition={springFade(spring.default)}
                                >
                                  {phase === 'approved' ? (
                                    <RiCheckLine size={15} aria-hidden="true" />
                                  ) : (
                                    <RiCloseLine size={15} aria-hidden="true" />
                                  )}
                                  {t(`${item}.${phase === 'approved' ? 'approved' : 'rejected'}`)}
                                </motion.p>
                              )}
                            </AnimatePresence>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  )}
                </div>

                {/* ---------- Soru düğmeleri ---------- */}
                <div className="talk-ask">
                  <p className="talk-ask-label" id={`${uid}-ask`}>
                    {t('prompt')}
                  </p>
                  <ul className="talk-chips" aria-labelledby={`${uid}-ask`}>
                    {KEYS.map((key) => (
                      <li key={key}>
                        <button
                          type="button"
                          className={`talk-chip${active === key ? ' talk-chip--on' : ''}`}
                          aria-pressed={active === key}
                          onClick={() => start(key)}
                        >
                          {t(`items.${key}.q`)}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            </div>

            <p className="talk-note">{t('note')}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
